import './style.css';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const finePointer = window.matchMedia('(pointer: fine)').matches;

// ==========================================================================
// WebGL Scene — lazy-loaded so three.js doesn't block first paint
// ==========================================================================

let scene = null;

import('./webgl/Scene.js').then(({ Scene }) => {
    const canvas = document.querySelector('#webgl');
    scene = new Scene(canvas, { static: reducedMotion });

    window.addEventListener('resize', () => scene.resize());

    if (!reducedMotion) {
        // Pause rendering once the user has scrolled well past the hero —
        // the fixed background barely moves, a frozen frame is imperceptible.
        window.addEventListener('scroll', () => {
            scene.setPaused(window.scrollY > window.innerHeight * 1.5);
        }, { passive: true });
    }
}).catch(() => {
    // WebGL unavailable — the site works fine on the plain dark background
});

// ==========================================================================
// Mobile Menu
// ==========================================================================

const navToggle = document.getElementById('navToggle');
const mobileMenu = document.getElementById('mobileMenu');

if (navToggle && mobileMenu) {
    const setMenu = (open) => {
        navToggle.setAttribute('aria-expanded', String(open));
        navToggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
        mobileMenu.setAttribute('aria-hidden', String(!open));
        mobileMenu.classList.toggle('open', open);
        document.body.classList.toggle('menu-open', open);
    };

    navToggle.addEventListener('click', () => {
        setMenu(navToggle.getAttribute('aria-expanded') !== 'true');
    });

    mobileMenu.querySelectorAll('a').forEach(link => {
        link.addEventListener('click', () => setMenu(false));
    });
}

// ==========================================================================
// Magnetic Hover Effect (desktop pointers only)
// ==========================================================================

if (finePointer && !reducedMotion) {
    document.querySelectorAll('.magnetic').forEach(el => {
        el.addEventListener('mousemove', (e) => {
            const rect = el.getBoundingClientRect();
            const x = e.clientX - rect.left - rect.width / 2;
            const y = e.clientY - rect.top - rect.height / 2;
            const strength = 0.3;

            gsap.to(el, { x: x * strength, y: y * strength, duration: 0.4, ease: 'power2.out' });
        });

        el.addEventListener('mouseleave', () => {
            gsap.to(el, { x: 0, y: 0, duration: 0.6, ease: 'elastic.out(1, 0.5)' });
        });
    });
}

// ==========================================================================
// Hero Entrance — masked word reveal
// ==========================================================================

if (!reducedMotion) {
    gsap.set('header', { y: -80, opacity: 0 });
    gsap.set('.badge', { y: 30, opacity: 0, scale: 0.9 });
    gsap.set('.hero h1 .w', { yPercent: 110 });
    gsap.set('.hero p', { y: 50, opacity: 0 });
    gsap.set('.cta .btn', { y: 40, opacity: 0, scale: 0.95 });
    gsap.set('.kpi', { y: 30, opacity: 0 });

    gsap.timeline({ defaults: { ease: 'power3.out', duration: 1 } })
        .to('header', { y: 0, opacity: 1, duration: 0.8 })
        .to('.badge', { y: 0, opacity: 1, scale: 1, duration: 0.6 }, '-=0.4')
        .to('.hero h1 .w', {
            yPercent: 0,
            duration: 1.1,
            stagger: 0.08,
            ease: 'power4.out'
        }, '-=0.3')
        .to('.hero p', { y: 0, opacity: 1, duration: 0.8 }, '-=0.7')
        .to('.cta .btn', { y: 0, opacity: 1, scale: 1, stagger: 0.15, duration: 0.6 }, '-=0.5')
        .to('.kpi', { y: 0, opacity: 1, stagger: 0.12, duration: 0.6 }, '-=0.3');

    // Hero content gently drifts and fades as you scroll into the page
    gsap.to('.hero > div', {
        y: -60,
        opacity: 0.25,
        ease: 'none',
        scrollTrigger: {
            trigger: '.hero',
            start: 'top top',
            end: 'bottom top',
            scrub: true
        }
    });
}

// ==========================================================================
// Scroll-Triggered Section Reveals
// ==========================================================================

if (!reducedMotion) {
    document.querySelectorAll('.sect').forEach(section => {
        const h2 = section.querySelector('h2');
        const content = section.querySelectorAll('p.muted, .grid, .contact');
        const features = section.querySelectorAll('.feature');
        const cards = section.querySelectorAll('.card');

        const tl = gsap.timeline({
            defaults: { ease: 'power3.out' },
            scrollTrigger: {
                trigger: section,
                start: 'top 80%',
                once: true
            }
        });

        if (h2) {
            tl.fromTo(h2, { y: 60, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8 });
        }

        if (content.length) {
            tl.fromTo(content,
                { y: 40, opacity: 0 },
                { y: 0, opacity: 1, duration: 0.7, stagger: 0.1 },
                '-=0.4'
            );
        }

        if (features.length) {
            tl.fromTo(features,
                { y: 60, opacity: 0, scale: 0.95 },
                { y: 0, opacity: 1, scale: 1, duration: 0.7, stagger: 0.12, ease: 'power2.out' },
                '-=0.3'
            );
        }

        if (cards.length) {
            tl.fromTo(cards,
                { y: 50, opacity: 0, clipPath: 'inset(10% 10% 10% 10% round 20px)' },
                { y: 0, opacity: 1, clipPath: 'inset(0% 0% 0% 0% round 20px)', duration: 0.9 },
                '-=0.4'
            );
        }
    });
}

// ==========================================================================
// App Card 3D Tilt (desktop pointers only)
// ==========================================================================

if (finePointer && !reducedMotion) {
    document.querySelectorAll('.app-card').forEach(card => {
        card.addEventListener('mousemove', (e) => {
            const rect = card.getBoundingClientRect();
            const px = (e.clientX - rect.left) / rect.width - 0.5;
            const py = (e.clientY - rect.top) / rect.height - 0.5;

            gsap.to(card, {
                rotateY: px * 6,
                rotateX: -py * 6,
                transformPerspective: 900,
                duration: 0.4,
                ease: 'power2.out'
            });
        });

        card.addEventListener('mouseleave', () => {
            gsap.to(card, { rotateY: 0, rotateX: 0, duration: 0.6, ease: 'power3.out' });
        });
    });
}

// ==========================================================================
// Hover Glows (buttons, cards, KPIs, logo)
// ==========================================================================

if (finePointer && !reducedMotion) {
    document.querySelectorAll('.btn.primary').forEach(btn => {
        btn.addEventListener('mouseenter', () => {
            gsap.to(btn, {
                boxShadow: '0 8px 40px rgba(255, 184, 0, 0.5), 0 0 60px rgba(255, 184, 0, 0.3)',
                duration: 0.4,
                ease: 'power2.out'
            });
        });
        btn.addEventListener('mouseleave', () => {
            gsap.to(btn, {
                boxShadow: '0 4px 15px rgba(255, 184, 0, 0.4), 0 0 0 rgba(255, 184, 0, 0)',
                duration: 0.5,
                ease: 'power2.out'
            });
        });
    });

    document.querySelectorAll('.kpi').forEach(kpi => {
        kpi.addEventListener('mouseenter', () => {
            gsap.to(kpi, { scale: 1.03, duration: 0.3, ease: 'power2.out' });
        });
        kpi.addEventListener('mouseleave', () => {
            gsap.to(kpi, { scale: 1, duration: 0.4, ease: 'power2.out' });
        });
    });

    const logo = document.querySelector('.logo');
    if (logo) {
        logo.addEventListener('mouseenter', () => {
            gsap.to(logo, { rotation: -8, scale: 1.1, duration: 0.4, ease: 'power2.out' });
        });
        logo.addEventListener('mouseleave', () => {
            gsap.to(logo, { rotation: 0, scale: 1, duration: 0.5, ease: 'elastic.out(1, 0.5)' });
        });
    }
}
